import axios from 'axios'
const IMAGE_BASE_URL = 'https://www.erplus.co/image'
const VIDEO_FILE_SIZE_LIMIT = 200 * 1024 * 1024
const UPLOAD_ATTACHMENT_URL = process.env.NODE_ENV === 'development'
  ? '/upload/uploadAttachment'
  : 'https://www.erplus.co/upload/uploadAttachment'
const VIDEO_UPLOAD_AUTH_URL = process.env.NODE_ENV === 'development'
  ? '/upload/videoUploadAuth'
  : 'https://www.erplus.co/upload/videoUploadAuth'
const ALIYUN_OSS_SDK_URL = 'https://gosspublic.alicdn.com/aliyun-oss-sdk-6.17.1.min.js'
const VIDEO_ATTACHMENT_REG = /(Video|erplusVideo)|\.(mp4|mov|m4v|webm)(\?|#|$)/i
const DIRECT_VIDEO_REG = /\.(mp4|mov|m4v|webm)(\?|#|$)/i

let ossSdkPromise = null

function loadScript (url) {
  return new Promise((resolve, reject) => {
    const loadedScript = document.querySelector(`script[src="${url}"]`)
    if (loadedScript) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = url
    script.onload = resolve
    script.onerror = reject
    document.body.appendChild(script)
  })
}

function loadOssSdk () {
  if (window.OSS) {
    return Promise.resolve()
  }
  if (!ossSdkPromise) {
    ossSdkPromise = loadScript(ALIYUN_OSS_SDK_URL)
  }
  return ossSdkPromise
}

function decodeBase64Json (value) {
  return JSON.parse(window.atob(value))
}

let formatFileMixin = {
  methods: {
    isImageFile (file) {
      const fileName = file && file.name ? file.name : ''
      return Boolean(file) && (
        file.type === 'image/jpeg' ||
        file.type === 'image/png' ||
        file.type === 'image/gif' ||
        file.type === 'image/jpg' ||
        /\.(jpg|jpeg|png|gif)$/i.test(fileName)
      )
    },
    isVideoFile (file) {
      const fileName = file && file.name ? file.name : ''
      return Boolean(file) && (
        file.type === 'video/mp4' ||
        file.type === 'video/quicktime' ||
        /\.(mp4|mov)$/i.test(fileName)
      )
    },
    isAcceptMediaFile (file) {
      return this.isImageFile(file) || this.isVideoFile(file)
    },
    isVideoAttachmentValue (value) {
      return VIDEO_ATTACHMENT_REG.test(String(value || ''))
    },
    isDirectVideoAttachmentValue (value) {
      return DIRECT_VIDEO_REG.test(String(value || ''))
    },
    shouldPairAdjacentMedia (currentValue, nextValue, isArrayInput) {
      if (isArrayInput || !nextValue || this.isVideoAttachmentValue(currentValue) || !this.isVideoAttachmentValue(nextValue)) {
        return false
      }
      return true
    },
    buildMediaSrc (url) {
      url = String(url || '')
      if (!url) {
        return ''
      }
      if (/^https?:\/\//i.test(url)) {
        return url
      }
      if (/^\/\//.test(url)) {
        return window.location.protocol + url
      }
      return IMAGE_BASE_URL + (url.charAt(0) === '/' ? url : '/' + url)
    },
    parseMediaPair (value) {
      const parts = String(value || '').split(',').map(item => item.trim()).filter(Boolean)
      if (parts.length < 2) {
        return null
      }
      const videoIndex = parts.findIndex(item => this.isVideoAttachmentValue(item))
      if (videoIndex === -1) {
        return null
      }
      return {
        coverUrl: parts.find((item, index) => index !== videoIndex && !this.isVideoAttachmentValue(item)) || '',
        sourceUrl: parts[videoIndex]
      }
    },
    buildVideoPlayerParams (src) {
      src = String(src || '')
      if (this.isDirectVideoAttachmentValue(src)) {
        return {sourceUrl: this.buildMediaSrc(src)}
      }
      return {videoId: src.split('.')[0]}
    },
    isVideoOverLimit (file) {
      return this.isVideoFile(file) && file.size > VIDEO_FILE_SIZE_LIMIT
    },
    buildFallbackVideoCoverFile (file) {
      return new Promise(resolve => {
        const canvas = document.createElement('canvas')
        canvas.width = 160
        canvas.height = 90
        const ctx = canvas.getContext('2d')
        if (!ctx || !canvas.toBlob) {
          resolve(null)
          return
        }
        ctx.fillStyle = '#111111'
        ctx.fillRect(0, 0, canvas.width, canvas.height)
        ctx.fillStyle = '#ffffff'
        ctx.beginPath()
        ctx.moveTo(68, 28)
        ctx.lineTo(68, 62)
        ctx.lineTo(98, 45)
        ctx.closePath()
        ctx.fill()
        canvas.toBlob(blob => {
          if (!blob) {
            resolve(null)
            return
          }
          const coverName = (file.name || 'video').replace(/\.[^.]+$/, '') + '-cover.jpg'
          resolve(new File([blob], coverName, {type: 'image/jpeg'}))
        }, 'image/jpeg', 0.82)
      })
    },
    buildVideoCoverFile (file, videoSrc) {
      return new Promise(resolve => {
        const video = document.createElement('video')
        let isResolved = false
        const finish = coverFile => {
          if (isResolved) {
            return
          }
          isResolved = true
          clearTimeout(timer)
          video.removeAttribute('src')
          video.load()
          if (coverFile) {
            resolve(coverFile)
            return
          }
          this.buildFallbackVideoCoverFile(file).then(resolve).catch(() => resolve(null))
        }
        const timer = setTimeout(() => finish(null), 5000)
        video.muted = true
        video.preload = 'metadata'
        video.playsInline = true
        video.onloadedmetadata = () => {
          const duration = Number.isFinite(video.duration) ? video.duration : 0
          video.currentTime = Math.min(0.1, duration || 0.1)
        }
        video.onseeked = () => {
          const canvas = document.createElement('canvas')
          canvas.width = video.videoWidth || 160
          canvas.height = video.videoHeight || 90
          const ctx = canvas.getContext('2d')
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
          canvas.toBlob(blob => {
            if (!blob) {
              finish(null)
              return
            }
            const coverName = (file.name || 'video').replace(/\.[^.]+$/, '') + '-cover.jpg'
            finish(new File([blob], coverName, {type: 'image/jpeg'}))
          }, 'image/jpeg', 0.82)
        }
        video.onerror = () => finish(null)
        video.src = videoSrc
      })
    },
    buildLocalVideoFile (file) {
      const videoSrc = URL.createObjectURL(file)
      return this.buildVideoCoverFile(file, videoSrc).then(coverFile => {
        return {src: videoSrc, file, isVideo: true, localUrl: videoSrc, coverFile}
      }).catch(() => {
        return {src: videoSrc, file, isVideo: true, localUrl: videoSrc}
      })
    },
    buildMediaFile (url, index, sourceUrl) {
      const mediaPair = this.parseMediaPair(url)
      if (mediaPair) {
        url = mediaPair.coverUrl
        sourceUrl = mediaPair.sourceUrl
      }
      if (!sourceUrl && this.isVideoAttachmentValue(url)) {
        sourceUrl = url
        url = ''
      }
      const videoSrc = sourceUrl && this.isDirectVideoAttachmentValue(sourceUrl)
        ? this.buildMediaSrc(sourceUrl)
        : ''
      const isVideo = Boolean(sourceUrl)
      return {
        id: index,
        url: url,
        src: url ? this.buildMediaSrc(url) : '',
        videoSrc: videoSrc,
        sourceUrl: sourceUrl || '',
        isVideo: isVideo,
        isUploaded: true
      }
    },
    formatMediaListFromCodes (files) {
      const list = []
      if (!files) {
        return list
      }
      const isArrayInput = Object.prototype.toString.call(files) === '[object Array]'
      let fileCodes = isArrayInput ? files : String(files).split(',')
      for (let i = 0; i < fileCodes.length; i++) {
        let file = fileCodes[i]
        if (!file) {
          continue
        }
        if (typeof file === 'object') {
          if (file.file || file.src) {
            list.push(Object.assign({}, file, {
              id: list.length,
              isVideo: Boolean(file.isVideo || file.sourceUrl),
              isUploaded: file.isUploaded !== false
            }))
          } else {
            list.push(this.formatUploadFile(file, [], list.length))
          }
          continue
        }
        const mediaPair = this.parseMediaPair(file)
        if (mediaPair) {
          list.push(this.buildMediaFile(mediaPair.coverUrl, list.length, mediaPair.sourceUrl))
          continue
        }
        if (this.shouldPairAdjacentMedia(file, fileCodes[i + 1], isArrayInput)) {
          list.push(this.buildMediaFile(file, list.length, fileCodes[i + 1]))
          i += 1
          continue
        }
        list.push(this.buildMediaFile(file, list.length))
      }
      return list
    },
    formatAttachment (files) {
      return this.formatMediaListFromCodes(files)
    },
    formatFileArrayToString (files) {
      if (Object.prototype.toString.call(files) === '[object Array]') {
        return files.map(file => {
          if (file.sourceUrl) { // sourceUrl属性表示视频的资源地址
            return [file.url, file.sourceUrl].filter(Boolean).join(',')
          }
          return file.url
        })
      } else {
        return files
      }
    }
  }
}

let fetchUploadFileMixin = {
  data () {
    return {
      isAllFilesUploaded: true
    }
  },
  methods: {
    fetchUploadPicture (imageList, callback) {
      if (!imageList.length) {
        callback()
        return false
      }
      let isAllFilesUploaded = imageList.every(image => image.isUploaded)
      if (isAllFilesUploaded) {
        this.isAllFilesUploaded = isAllFilesUploaded
        callback()
        return false
      }
      this.isAllFilesUploaded = false
      Promise.all(imageList.map((file, index) => {
        if (file.isUploaded) {
          return Promise.resolve(file)
        }
        if (file.isVideo || this.isVideoFile(file.file)) {
          return this.uploadVodVideoAttachment(file, index)
        }
        return this.uploadNormalAttachment(file, index)
      })).then(uploadedFiles => {
        imageList.splice(0, imageList.length, ...uploadedFiles)
        this.isAllFilesUploaded = true
        callback()
      }).catch(error => {
        this.isAllFilesUploaded = false
        this.resetUploadingState()
        this.$message.warning(error && error.message ? error.message : '上传图片/视频失败')
      })
    },
    resetUploadingState () {
      if (Object.prototype.hasOwnProperty.call(this, 'isSubmitQuestion')) {
        this.isSubmitQuestion = false
      }
      if (Object.prototype.hasOwnProperty.call(this, 'isSubmitMessage')) {
        this.isSubmitMessage = false
      }
    },
    uploadNormalAttachment (file, index) {
      return this.uploadAttachmentFiles([file.file]).then(uploadItems => {
        return this.formatUploadFile(file, uploadItems, index)
      })
    },
    uploadAttachmentFiles (files) {
      let formData = new FormData()
      files.forEach(file => {
        if (file) {
          formData.append('file[]', file)
        }
      })
      return axios({
        method: 'post',
        baseURL: '',
        url: UPLOAD_ATTACHMENT_URL,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        data: formData,
        timeout: 1000 * 60 * 5
      }).then(res => {
        const pictureRes = res.data
        if (pictureRes.respCode !== '000') {
          throw new Error(pictureRes.respDesc || '上传图片失败')
        }
        if (!pictureRes.item || !pictureRes.item.length) {
          throw new Error('上传图片失败')
        }
        return pictureRes.item.slice()
      })
    },
    uploadVodVideoAttachment (file, index) {
      return this.buildVideoCoverForUpload(file).then(coverFile => {
        return Promise.all([
          this.uploadAttachmentFiles([coverFile]),
          this.uploadVodVideo(file.file)
        ])
      }).then(([coverItems, videoId]) => {
        const coverUrl = String(coverItems[0] || '')
        if (!coverUrl) {
          throw new Error('上传视频封面失败')
        }
        if (!videoId) {
          throw new Error('上传视频失败')
        }
        return this.buildUploadedVideoFile(file, coverUrl, videoId + '.erplusVideo', index)
      })
    },
    buildVideoCoverForUpload (file) {
      if (file.coverFile) {
        return Promise.resolve(file.coverFile)
      }
      return this.buildFallbackVideoCoverFile(file.file).then(coverFile => {
        if (!coverFile) {
          throw new Error('生成视频封面失败')
        }
        return coverFile
      })
    },
    uploadVodVideo (file) {
      return this.getVideoUploadAuth(file).then(uploadData => {
        return loadOssSdk().then(() => this.uploadFileToVodOss(file, uploadData))
      })
    },
    getVideoUploadAuth (file) {
      const fileName = file.name || 'video.mp4'
      return axios({
        method: 'get',
        baseURL: '',
        url: VIDEO_UPLOAD_AUTH_URL,
        params: {
          title: fileName.replace(/\.[^.]+$/, '') || fileName,
          fileName: fileName
        }
      }).then(res => {
        const data = res.data
        if (data.erpCode !== '000' || !data.erpData) {
          throw new Error(data.erpMsg || '获取视频上传凭证失败')
        }
        return data.erpData
      })
    },
    uploadFileToVodOss (file, uploadData) {
      return new Promise((resolve, reject) => {
        try {
          const uploadAddress = decodeBase64Json(uploadData.address)
          const uploadAuth = decodeBase64Json(uploadData.auth)
          const endpoint = uploadAddress.Endpoint || ''
          const region = uploadAuth.Region ? 'oss-' + uploadAuth.Region : 'oss-cn-shanghai'
          const client = new window.OSS({
            region: region,
            endpoint: endpoint,
            secure: /^https:\/\//i.test(endpoint),
            accessKeyId: uploadAuth.AccessKeyId,
            accessKeySecret: uploadAuth.AccessKeySecret,
            stsToken: uploadAuth.SecurityToken,
            bucket: uploadAddress.Bucket
          })
          client.multipartUpload(uploadAddress.FileName, file).then(() => {
            resolve(uploadData.videoId)
          }).catch(reject)
        } catch (error) {
          reject(error)
        }
      })
    },
    formatUploadFile (file, uploadItems, index) {
      let url = uploadItems.shift()
      if (url && typeof url === 'object') {
        const mediaPair = this.parseMediaPair(url.fileCode || url.attachment || url.attachments)
        const sourceUrl = url.sourceUrl || url.videoId || url.videoUrl || (mediaPair && mediaPair.sourceUrl) || ''
        url = url.coverUrl || (mediaPair && mediaPair.coverUrl) || url.url || url.fileName || url.name || ''
        if (sourceUrl) {
          return this.buildUploadedVideoFile(file, url, sourceUrl, index)
        }
      }
      url = String(url || '')
      const mediaPair = this.parseMediaPair(url)
      if (mediaPair) {
        return this.buildUploadedVideoFile(file, mediaPair.coverUrl, mediaPair.sourceUrl, index)
      }
      if (file.isVideo || this.isVideoFile(file.file)) {
        if (this.isVideoAttachmentValue(url)) {
          return this.buildUploadedVideoFile(file, '', url, index)
        }
        const nextUrl = String(uploadItems[0] || '')
        if (this.isVideoAttachmentValue(nextUrl)) {
          uploadItems.shift()
          return this.buildUploadedVideoFile(file, url, nextUrl, index)
        }
      }
      return {
        id: index,
        url: url,
        src: this.buildMediaSrc(url),
        isUploaded: true
      }
    },
    buildUploadedVideoFile (file, coverUrl, sourceUrl, index) {
      return {
        id: index,
        url: coverUrl,
        src: coverUrl ? this.buildMediaSrc(coverUrl) : file.src,
        videoSrc: sourceUrl && this.isDirectVideoAttachmentValue(sourceUrl) ? this.buildMediaSrc(sourceUrl) : '',
        sourceUrl: sourceUrl,
        isVideo: true,
        isUploaded: true
      }
    }
  }
}

export {
  formatFileMixin,
  fetchUploadFileMixin
}



// WEBPACK FOOTER //
// ./src/mixins/format-file.js